/**
 * Free Gift Manager
 *
 * Automatically adds/removes a free gift product based on qualifying
 * products in the cart. Qualifying products are identified by a hidden
 * line item property `_free_gift_qualifier` set during add-to-cart.
 *
 * Configuration is read from a <script type="application/json" id="free-gift-config"> element.
 */
(function () {
  'use strict';

  const configEl = document.getElementById('free-gift-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[FreeGift] Config parse error:', e);
    return;
  }

  if (!config.giftVariantId) return;

  let processing = false;

  /**
   * Main handler — called on every cart change and on page load.
   * Checks whether the gift should be added or removed.
   */
  async function onCartChange(event) {
    // Skip events we dispatched ourselves to avoid infinite loops
    if (event && event.detail && event.detail.data && event.detail.data.source === 'free-gift-manager') {
      return;
    }

    if (processing) return;
    processing = true;

    try {
      const cart = await fetchCart();
      const hasQualifier = cartHasQualifyingProduct(cart);
      const giftItem = findGiftItem(cart);

      if (hasQualifier && !giftItem) {
        await addGift();
      } else if (!hasQualifier && giftItem) {
        await removeGift(giftItem);
      }
    } catch (error) {
      console.error('[FreeGift] Error:', error);
    } finally {
      processing = false;
    }
  }

  /**
   * Fetch current cart state.
   */
  async function fetchCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  /**
   * Check if any cart item (other than the gift itself) has the qualifying property.
   */
  function cartHasQualifyingProduct(cart) {
    return cart.items.some(function (item) {
      // Don't count the gift as a qualifier
      if (item.properties && item.properties._free_gift === 'true') return false;
      // Check for the qualifier stamp
      return item.properties && item.properties._free_gift_qualifier === 'true';
    });
  }

  /**
   * Find the gift line item in the cart by its `_free_gift` property.
   */
  function findGiftItem(cart) {
    return cart.items.find(function (item) {
      return item.properties && item.properties._free_gift === 'true';
    });
  }

  /**
   * Add the free gift to the cart.
   */
  async function addGift() {
    var body = {
      items: [
        {
          id: parseInt(config.giftVariantId, 10),
          quantity: 1,
          properties: {
            _free_gift: 'true',
          },
        },
      ],
    };

    var response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      refreshCartUI();
    }
  }

  /**
   * Remove the free gift from the cart by setting its quantity to 0.
   */
  async function removeGift(giftItem) {
    var body = {
      updates: {},
    };
    body.updates[giftItem.key] = 0;

    var response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      refreshCartUI();
    }
  }

  /**
   * Dispatch a cart:update event so the theme re-renders the cart UI.
   * Includes a source identifier so we can skip our own events.
   */
  function refreshCartUI() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: { source: 'free-gift-manager' },
        },
      })
    );
  }

  // Listen for all cart change events (CartAddEvent and CartUpdateEvent both use 'cart:update')
  document.addEventListener('cart:update', onCartChange);

  // Also run on page load to handle refresh / direct navigation to /cart
  onCartChange(null);
})();
